import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Input } from '@/shared/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { Separator } from '@/shared/components/ui/separator';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { UserAvatar } from '@/shared/components/custom/avatars/UserAvatar';
import { useProjectMembersSearch } from '@/features/projects/public';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { PALETTE } from '@/core/lib/color-palette';
import type { TaskAssignee } from '../../types';

const MEMBERS_PAGE_SIZE = 20;

function deterministicColor(userId: string): string {
  const hash = userId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return PALETTE[hash % PALETTE.length].token;
}

// ── Internal: single row in the "All users" section ──────────────────────────

interface MemberRowProps {
  userId: string;
  displayName: string;
  avatarUrl?: string | null;
  onAssign: () => void;
}

function MemberRow({ userId, displayName, avatarUrl, onAssign }: MemberRowProps) {
  const { t } = useTranslation('tasks');
  const [hovered, setHovered] = useState(false);
  const color = deterministicColor(userId);

  return (
    <div
      data-testid={`tasks-assignee-picker-all-item-${userId}`}
      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-accent"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onAssign}
    >
      <UserAvatar name={displayName} color={color} src={avatarUrl ?? undefined} size="xs" />
      <span className="flex-1 text-xs">{displayName}</span>
      {hovered && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              data-testid={`tasks-assignee-picker-assign-btn-${userId}`}
              className="flex h-5 w-5 items-center justify-center rounded hover:text-primary"
              onClick={(e) => {
                e.stopPropagation();
                onAssign();
              }}
              aria-label={t('assigneePicker.assignAria')}
            >
              <RefreshCw className="size-3" />
            </button>
          </TooltipTrigger>
          <TooltipContent>{t('assigneePicker.assignAria')}</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface AssigneePickerProps {
  projectId: string;
  assignee: TaskAssignee | null;
  onSelect: (assigneeId: string | null) => void;
  disabled?: boolean;
  /** Overrides the trigger's empty-state label (defaults to "no assignee"). */
  placeholder?: string;
}

export function AssigneePicker({
  projectId,
  assignee,
  onSelect,
  disabled,
  placeholder,
}: AssigneePickerProps) {
  const { t } = useTranslation('tasks');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [pageSize, setPageSize] = useState(MEMBERS_PAGE_SIZE);
  const [lastSearch, setLastSearch] = useState(debouncedSearch);

  // A new search term invalidates how many results are "loaded so far" — restart
  // from the first page instead of keeping whatever page size scrolling had grown to.
  // Reset during render (not an effect) per the "adjusting state on prop change" pattern.
  if (debouncedSearch !== lastSearch) {
    setLastSearch(debouncedSearch);
    setPageSize(MEMBERS_PAGE_SIZE);
  }

  const {
    data: members,
    totalCount,
    isLoading,
    isFetching,
  } = useProjectMembersSearch(projectId, debouncedSearch, 0, pageSize);

  const unassignedMembers = members.filter((m) => m.userId !== assignee?.id);
  const hasMore = members.length < totalCount;

  function handleListScroll(e: React.UIEvent<HTMLDivElement>) {
    if (!hasMore || isFetching) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
      setPageSize((n) => n + MEMBERS_PAGE_SIZE);
    }
  }

  function handleSelect(userId: string | null) {
    onSelect(userId);
    setOpen(false);
    setSearch('');
  }

  function handleOpenChange(v: boolean) {
    setOpen(v);
    if (!v) setSearch('');
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          data-testid="tasks-assignee-picker-trigger"
          className="flex min-w-0 items-center gap-1.5 text-xs transition-opacity hover:opacity-70"
        >
          {assignee ? (
            <>
              <UserAvatar
                name={assignee.name}
                color={deterministicColor(assignee.id)}
                src={assignee.avatarUrl}
                size="xs"
              />
              <span className="min-w-0 truncate">{assignee.name}</span>
            </>
          ) : (
            <span className="text-muted-foreground">{placeholder ?? t('metadata.noAssignee')}</span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="w-64 p-0"
        align="start"
        data-testid="tasks-assignee-picker-popover"
      >
        <div className="px-3 py-2 text-xs font-semibold">{t('assigneePicker.title')}</div>
        <Separator />

        <div className="px-2 pt-2 pb-1">
          <Input
            data-testid="tasks-assignee-picker-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('assigneePicker.searchPlaceholder')}
            className="h-7 text-xs"
          />
        </div>

        {assignee && (
          <>
            <div className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              {t('assigneePicker.selectedTitle')}
            </div>
            <div data-testid="tasks-assignee-picker-selected-section" className="px-1 pb-1">
              <button
                type="button"
                data-testid="tasks-assignee-picker-selected-item"
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 bg-accent/40 hover:bg-accent"
                onClick={() => handleSelect(null)}
              >
                <UserAvatar
                  name={assignee.name}
                  color={deterministicColor(assignee.id)}
                  src={assignee.avatarUrl}
                  size="xs"
                />
                <span className="flex-1 text-xs font-medium">{assignee.name}</span>
              </button>
            </div>
            <Separator />
          </>
        )}

        <div className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
          {t('assigneePicker.allTitle')}
        </div>
        <div
          data-testid="tasks-assignee-picker-all-section"
          className="max-h-48 overflow-y-auto px-1 pb-1"
          onScroll={handleListScroll}
        >
          {isLoading ? (
            <div className="flex flex-col gap-1.5 px-2 py-1.5">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-2">
                  <Skeleton className="size-5 rounded-full" />
                  <Skeleton className="h-3 w-24 rounded" />
                </div>
              ))}
            </div>
          ) : unassignedMembers.length === 0 ? (
            <p className="py-3 text-center text-xs text-muted-foreground">
              {t('assigneePicker.noResults')}
            </p>
          ) : (
            <>
              {unassignedMembers.map((m) => (
                <MemberRow
                  key={m.userId}
                  userId={m.userId}
                  displayName={m.displayName}
                  avatarUrl={m.avatarUrl}
                  onAssign={() => handleSelect(m.userId)}
                />
              ))}
              {isFetching && (
                <div className="flex justify-center py-1.5">
                  <Skeleton className="h-3 w-16 rounded" />
                </div>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
